/*
 * wb-search-enhancer.js
 * Defines <wb-predictive-search>, a drop-in replacement for the bundled
 * <predictive-search> element. Adds:
 *   - Typo correction for common Wall Blush mis-spellings
 *   - Stopword stripping ("wallpaper" etc. are implicit on this store)
 *   - Synonym-driven retry when a query returns zero results
 *   - "Did you mean" hint on retry hits
 *   - Match-term highlighting in the results panel
 *   - Recent + popular searches as the idle state (localStorage-backed)
 *
 * The element uses the same shadow-DOM slot architecture as the original
 * (slots: idle / loading / results) so the section template stays simple.
 */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('wb-predictive-search')) {
    return;
  }

  var DEBOUNCE_MS = 220;
  var RECENT_KEY = 'wb:recentSearches';
  var RECENT_MAX = 5;
  var RESULTS_MARKER = 'data-wb-search-results';

  var TYPO_FIXES = {
    wallpaer: 'wallpaper',
    wallpapper: 'wallpaper',
    wllpaper: 'wallpaper',
    walpaper: 'wallpaper',
    wlpaper: 'wallpaper',
    peal: 'peel',
    polkadots: 'polka dots',
    polcadot: 'polka dot',
    boheamian: 'bohemian',
    bohomian: 'bohemian',
    midcentry: 'mid-century',
    walblush: 'wall blush',
    flural: 'floral',
    florel: 'floral',
    gemoetric: 'geometric',
    geomtric: 'geometric',
    butterfy: 'butterfly',
    butterflys: 'butterflies'
  };

  // Removed only when other meaningful tokens remain.
  var STOPWORDS = {
    wallpaper: 1,
    wallpapers: 1,
    wallcovering: 1,
    wallcoverings: 1,
    paper: 1,
    the: 1,
    a: 1,
    an: 1,
    for: 1,
    with: 1,
    of: 1,
    and: 1
  };

  // Bidirectional synonym groups. Used to build a retry query when the
  // primary search returns zero results. Order within each group is the
  // preferred replacement order.
  var SYNONYM_GROUPS = [
    ['floral', 'flower', 'flowers', 'botanical', 'florals'],
    ['kids', 'kid', 'nursery', 'baby', 'children', 'playroom'],
    ['peel and stick', 'removable', 'peel-and-stick', 'self adhesive', 'self-adhesive', 'temporary'],
    ['stripe', 'striped', 'stripes', 'lines'],
    ['polka dot', 'polka dots', 'dots', 'dotted', 'spots', 'spotted'],
    ['boho', 'bohemian'],
    ['farmhouse', 'rustic', 'country'],
    ['mid-century', 'midcentury', 'mid century', 'mcm'],
    ['art deco', 'art-deco', 'deco'],
    ['neutral', 'neutrals', 'beige', 'cream', 'tan'],
    ['powder room', 'half bath', 'guest bath'],
    ['accent wall', 'feature wall', 'statement wall'],
    ['bathroom', 'bath'],
    ['plaid', 'tartan', 'check', 'checkered'],
    ['geometric', 'geometrical', 'angular'],
    ['mural', 'murals', 'large scale', 'oversized'],
    ['butterfly', 'butterflies'],
    ['sample', 'samples', 'swatch', 'swatches']
  ];

  var SYNONYM_INDEX = (function () {
    var idx = Object.create(null);
    SYNONYM_GROUPS.forEach(function (group) {
      group.forEach(function (term) { idx[term] = group; });
    });
    return idx;
  })();

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function escapeRegex(s) {
    return s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  }

  function normalizeQuery(raw) {
    if (!raw) return { primary: '', tokens: [] };
    var cleaned = raw
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!cleaned) return { primary: '', tokens: [] };

    var rawTokens = cleaned.split(' ');
    var fixed = rawTokens.map(function (t) { return TYPO_FIXES[t] || t; });
    // Re-split because typo fixes may introduce spaces ("polkadots" -> "polka dots")
    var tokens = fixed.join(' ').split(' ').filter(Boolean);
    var meaningful = tokens.filter(function (t) { return !STOPWORDS[t]; });
    var finalTokens = meaningful.length > 0 ? meaningful : tokens;
    return { primary: finalTokens.join(' '), tokens: finalTokens };
  }

  // Try to swap one token in `tokens` for a synonym. Also handles
  // two-word phrases ("peel and stick" etc.).
  function buildSynonymRetry(tokens) {
    // Phrase pass: scan adjacent 2- and 3-word windows for phrase synonyms
    for (var window = 3; window >= 2; window--) {
      for (var i = 0; i <= tokens.length - window; i++) {
        var phrase = tokens.slice(i, i + window).join(' ');
        var group = SYNONYM_INDEX[phrase];
        if (group) {
          var alt = group.find(function (s) { return s !== phrase; });
          if (alt) {
            var copy = tokens.slice();
            copy.splice(i, window, alt);
            return copy.join(' ');
          }
        }
      }
    }
    // Single-token pass
    for (var j = 0; j < tokens.length; j++) {
      var g = SYNONYM_INDEX[tokens[j]];
      if (g) {
        var altT = g.find(function (s) { return s !== tokens[j]; });
        if (altT) {
          var c = tokens.slice();
          c[j] = altT;
          return c.join(' ');
        }
      }
    }
    return null;
  }

  function readRecent() {
    try {
      var raw = localStorage.getItem(RECENT_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
    } catch (e) { return []; }
  }

  function pushRecent(term) {
    if (!term) return;
    try {
      var list = readRecent();
      var next = [term].concat(list.filter(function (x) { return x !== term; })).slice(0, RECENT_MAX);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (e) { /* ignore */ }
  }

  class WbPredictiveSearchEl extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(
        document.createRange().createContextualFragment('<slot name="idle"></slot>')
      );
      this._didYouMean = null;
    }

    connectedCallback() {
      this._form = this.closest('form');
      if (!this._form) return;
      this._input = this._form.elements && this._form.elements['q'];
      if (!this._input) return;
      this._sectionId = this.getAttribute('section-id') || '';
      this._endpoint = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'search/suggest';

      this._renderIdleExtras();

      this._onInput = debounce(this._handleInput.bind(this), DEBOUNCE_MS);
      this._input.addEventListener('input', this._onInput);
      this._form.addEventListener('reset', this._onReset.bind(this));
      this._form.addEventListener('submit', this._onSubmit.bind(this));
    }

    _setSlot(name) {
      var slot = this.shadowRoot.firstElementChild;
      if (slot.getAttribute('name') === name) return;
      slot.setAttribute('name', name);
    }

    _renderIdleExtras() {
      var holder = this.querySelector('[data-wb-recent-searches]');
      if (!holder) return;
      var recent = readRecent();
      if (recent.length === 0) {
        holder.hidden = true;
        return;
      }
      holder.hidden = false;
      holder.innerHTML = recent.map(function (term) {
        return '<li><a href="' + this._suggestHref(term) + '" data-wb-recent-term>' + this._escape(term) + '</a></li>';
      }.bind(this)).join('');
    }

    _suggestHref(term) {
      return '/search?type=product&q=' + encodeURIComponent(term);
    }

    _escape(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    _onReset() {
      this._abort();
      this._didYouMean = null;
      this._renderIdleExtras();
      this._setSlot('idle');
    }

    _onSubmit() {
      var raw = (this._input.value || '').trim();
      if (raw) pushRecent(raw);
    }

    async _handleInput() {
      var raw = this._input.value || '';
      if (!raw.trim()) {
        this._abort();
        return this._setSlot('idle');
      }

      var norm = normalizeQuery(raw);
      if (!norm.primary) return this._setSlot('idle');

      this._abort();
      this._abortController = new AbortController();
      this._setSlot('loading');

      try {
        var html = await this._fetchResults(norm.primary, this._abortController.signal);
        var hasHits = this._hasResults(html);
        this._didYouMean = null;

        if (!hasHits) {
          var retry = buildSynonymRetry(norm.tokens);
          if (retry && retry !== norm.primary) {
            var retryHtml = await this._fetchResults(retry, this._abortController.signal);
            if (this._hasResults(retryHtml)) {
              html = retryHtml;
              this._didYouMean = retry;
            }
          }
        }

        this._renderResults(html);
        this._setSlot('results');
      } catch (err) {
        if (err && err.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('[wb-search] fetch failed', err);
          this._setSlot('results');
        }
      }
    }

    async _fetchResults(q, signal) {
      var params = new URLSearchParams();
      params.set('q', q);
      params.set('section_id', this._sectionId);
      params.set('resources[limit]', '10');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
      params.set('resources[options][unavailable_products]', 'last');
      var url = this._endpoint + '?' + params.toString();
      var res = await fetch(url, { signal: signal, headers: { Accept: 'text/html' } });
      return res.text();
    }

    _hasResults(html) {
      return html && html.indexOf(RESULTS_MARKER + '="1"') !== -1;
    }

    _renderResults(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[slot="results"]');
      var existing = this.querySelector('[slot="results"]');
      if (!fresh || !existing) return;
      existing.replaceWith(document.importNode(fresh, true));

      if (this._didYouMean) {
        var note = document.createElement('p');
        note.className = 'wb-search-dym';
        note.setAttribute('role', 'status');
        note.textContent = 'Showing results for "' + this._didYouMean + '"';
        var target = this.querySelector('[slot="results"]');
        if (target) target.prepend(note);
      }

      this._highlight();
    }

    _highlight() {
      var raw = (this._input.value || '').trim();
      if (!raw) return;
      var tokens = raw.toLowerCase().split(/\s+/).filter(function (t) { return t.length > 1; });
      if (!tokens.length) return;
      var re = new RegExp('(' + tokens.map(escapeRegex).join('|') + ')', 'gi');
      var nodes = this.querySelectorAll('[data-wb-search-hl]');
      nodes.forEach(function (el) {
        var text = el.textContent;
        // Use textContent assignment first to defang prior HTML, then rebuild
        el.textContent = text;
        el.innerHTML = el.innerHTML.replace(re, '<mark>$1</mark>');
      });
    }

    _abort() {
      if (this._abortController) {
        try { this._abortController.abort(); } catch (e) { /* ignore */ }
        this._abortController = null;
      }
    }
  }

  window.customElements.define('wb-predictive-search', WbPredictiveSearchEl);
})();
